import { globalStores } from '@/editor/stores';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { debounce } from 'lodash-es';
import { EEntityType, ESceneMode, ISelected } from '@/editor/types';
import { RegionTextInfoEntity } from '@/editor/entities/regionTextInfoEntity';
import { RoomTextInfoEntity } from '@/editor/entities/roomTextInfoEntity';
import { EAutoLayoutMode } from '@/editor/utils/layout/detect';
import { AreaDimensionTextInfoEntity } from '@/editor/entities/areaDimensionTextInfoEntity';
import { IAreaDimension } from '@/editor/data/dimension/dimension';
import { DisposableBag } from '@/editor/controller/contextMenuController/disposableBag';
import { RoomData } from '@/editor/data/room/Room';
import { RegionData } from '@/editor/data/region/Region';

export class TextInfoManager {
  private _roomLabelStore: LabelEntityStore<RoomData, RoomTextInfoEntity> = new LabelEntityStore({
    getId: (data) => data.id,
    create: (data) => new RoomTextInfoEntity(this._buildRoomLabelParams(data)),
    update: (entity, data) => entity.update(this._buildRoomLabelParams(data))
  });
  private _regionLabelStore: LabelEntityStore<RegionData, RegionTextInfoEntity> = new LabelEntityStore({
    getId: (data) => data.id,
    create: (data) => new RegionTextInfoEntity(this._buildRegionLabelParams(data)),
    update: (entity, data) => entity.update(this._buildRegionLabelParams(data))
  });
  private _areaDimensionStore = new LabelEntityStore<IAreaDimension, AreaDimensionTextInfoEntity>({
    getId: (data) => data.id,
    create: (data) => new AreaDimensionTextInfoEntity(data),
    update: (entity, data) => entity.update(data)
  });
  private _tempVisible = true;
  private _disposableBag = new DisposableBag();

  constructor() {
    this._disposableBag
      .add(editorEvent.on(EditorEventName.ENTITY_SELECTED, this._handleEntitySelected))
      .add(editorEvent.on(EditorEventName.VIEW2D_CHANGE, this._hideTemporarily))
      .add(editorEvent.on(EditorEventName.SCENE_MODE_CHANGE, this._hideTemporarily))
      .add(editorEvent.on(EditorEventName.RESIZE, this._hideTemporarily));
  }

  private get _isSceneModeShouldShow() {
    return globalStores.sceneMode === ESceneMode.scene2d;
  }

  private _deboucedShow = debounce(() => {
    if (this._isSceneModeShouldShow) {
      this._setVisible(true);
    }
  }, 500);

  private _hideTemporarily = () => {
    this._setVisible(false);
    this._deboucedShow();
  };

  /** 设置全部标签的显隐状态，只对最外层的 Container 做隐藏 */
  private _setVisible(value: boolean) {
    if (value === this._tempVisible) return;

    this._tempVisible = value;
    globalStores.editorStateStore.textInfoContainer.style.display = value ? 'block' : 'none';
    if (value) {
      this._updatePosition();
    }
  }

  private _buildRoomLabelParams(data: RoomData) {
    return {
      data,
      name: data.roomInfo.name,
      withRegion: globalStores.regionStore.getRoomRegions(data.id).length > 1,
      visible: this._roomLabelStore.visible
    };
  }

  private _buildRegionLabelParams(data: RegionData) {
    return {
      data,
      name: data.regionInfo.name,
      visible: this._regionLabelStore.visible
    };
  }

  private _handleEntitySelected = (selected: ISelected) => {
    switch (selected.type) {
      case EEntityType.Room: {
        const isLayoutMode = globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE;
        this._areaDimensionStore.setSelected(null);
        this._roomLabelStore.setSelected(!isLayoutMode ? selected.entityId : null);
        break;
      }
      case EEntityType.AreaDimension: {
        this._roomLabelStore.setSelected(null);
        this._areaDimensionStore.setSelected(selected.entityId);
        break;
      }
      default: {
        this._roomLabelStore.setSelected(null);
        this._areaDimensionStore.setSelected(null);
      }
    }
  };

  /** 设置分区标签的全局可见性 */
  public setRegionTextInfoVisible(visible: boolean) {
    this._regionLabelStore.setVisible(visible);

    if (visible) {
      this._updatePosition();
    }
  }

  /** 设置房间标签的全局可见性 */
  public setRoomTextInfoVisible(visible: boolean) {
    this._roomLabelStore.setVisible(visible);
    if (visible) {
      this._updatePosition();
    }
  }

  update() {
    const roomDatas = globalStores.roomStore.getRoomDatas();
    // 只有分区数大于 1 的房间才展示分区标签
    const regionDatas = roomDatas.flatMap((roomData) => {
      const regions = globalStores.regionStore.getRoomRegions(roomData.id);
      return regions.length > 1 ? regions : [];
    });

    this._roomLabelStore.update(roomDatas);
    this._regionLabelStore.update(regionDatas);
    this._syncAreaDimensionTextInfos();

    if (this._tempVisible && this._isSceneModeShouldShow) {
      this._updatePosition();
    }
  }

  public updateAreaDimensionTextInfos() {
    this._syncAreaDimensionTextInfos();

    if (this._tempVisible && this._isSceneModeShouldShow) {
      this._updatePosition();
    }
  }

  private _syncAreaDimensionTextInfos() {
    const hiddenIds = globalStores.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.AreaDimension);
    const areaDimensions = globalStores.dimensionStore
      .getAreaDimList()
      .filter((areaDimension) => !hiddenIds.has(areaDimension.id));

    this._areaDimensionStore.update(areaDimensions);
  }

  updateSingleRegion(regionId: string) {
    const regionData = globalStores.regionStore.getRegionData(regionId);
    if (regionData && this._regionLabelStore.updateSingle(regionData)) {
      if (this._tempVisible && this._isSceneModeShouldShow) {
        this._updatePosition();
      }
    }
  }

  updateSingleRoom(roomId: string) {
    const roomData = globalStores.roomStore.getRoomData(roomId);
    if (roomData && this._roomLabelStore.updateSingle(roomData)) {
      if (this._tempVisible && this._isSceneModeShouldShow) {
        this._updatePosition();
      }
    }
  }

  private _updatePosition = () => {
    if (!this._tempVisible) return;

    if (this._regionLabelStore.visible) {
      for (const entity of this._regionLabelStore.values()) {
        entity.updatePosition();
      }
    }

    if (this._roomLabelStore.visible) {
      for (const entity of this._roomLabelStore.values()) {
        const regionRects = this._regionLabelStore.visible
          ? this._getRoomRegionTextInfos(entity.id)
              .map((regionEntity) => regionEntity.getLabelRect())
              .filter((rect) => rect.width > 0 && rect.height > 0)
          : [];
        entity.updatePositionAvoiding(regionRects);
        entity.updateScale();
      }
    }

    for (const entity of this._areaDimensionStore.values()) {
      entity.updatePosition();
    }
  };

  private _getRoomRegionTextInfos(roomId: string) {
    const regions = globalStores.regionStore.getRoomRegions(roomId);
    const entities: RegionTextInfoEntity[] = [];
    for (const region of regions) {
      const entity = this._regionLabelStore.get(region.id);
      if (entity) {
        entities.push(entity);
      }
    }
    return entities;
  }

  setFocus({ id, type }: { id: string; type: 'room' | 'region' }) {
    if (type === 'room') {
      for (const entity of this._roomLabelStore.values()) {
        entity.updateInfoEnable(entity.id === id);
      }
      for (const entity of this._regionLabelStore.values()) {
        entity.updateInfoEnable(false);
      }
    } else if (type === 'region') {
      for (const entity of this._regionLabelStore.values()) {
        entity.updateInfoEnable(entity.getData().id === id);
      }
      for (const entity of this._roomLabelStore.values()) {
        entity.updateInfoEnable(false);
      }
    }
  }

  resetFocus() {
    for (const entity of this._regionLabelStore.values()) {
      entity.resetFocus();
    }
    for (const entity of this._roomLabelStore.values()) {
      entity.resetFocus();
    }
  }

  updateHover(roomId?: string) {
    if (roomId === undefined) return;

    this._roomLabelStore.setHover(roomId);
  }

  public updateAreaDimensionHover(areaDimensionId: string) {
    if (areaDimensionId === undefined) return;

    this._areaDimensionStore.setHover(areaDimensionId);
  }

  /**
   * 设置单个面积标注文本的可见性
   *
   * 供变换期「原件让位给虚拟件」使用，不改动 `_areaDimensionStore` 的全局可见性
   */
  public setAreaDimensionTextInfoVisible(areaDimensionId: string, visible: boolean) {
    this._areaDimensionStore.get(areaDimensionId)?.setVisible(visible);
  }

  dispose() {
    this._roomLabelStore.dispose();
    this._regionLabelStore.dispose();
    this._areaDimensionStore.dispose();
    this._disposableBag.dispose();
  }
}

interface ILabelEntity {
  dispose: () => void;
  setSelected?: (value: boolean) => void;
  setHover?: (value: boolean) => void;
  setVisible?: (value: boolean) => void;
}

class LabelEntityStore<TData, TEntity extends ILabelEntity> {
  private _map = new Map<string, TEntity>();
  private _selected: string | null = null;
  private _hovered: string | null = null;
  private _visible = true;

  get visible() {
    return this._visible;
  }

  constructor(
    private _options: {
      getId: (data: TData) => string;
      create: (data: TData) => TEntity;
      update: (entity: TEntity, data: TData) => void;
    }
  ) {}

  /** 声明式同步：销毁不在 datas 中的实体，更新已有实体，创建缺失实体 */
  update(datas: TData[]) {
    const nextIds = new Set(datas.map((data) => this._options.getId(data)));
    for (const [id, entity] of this._map) {
      if (nextIds.has(id)) continue;

      entity.dispose();
      this._map.delete(id);
    }

    for (const data of datas) {
      this.upsert(data);
    }
  }

  upsert(data: TData): TEntity {
    const id = this._options.getId(data);
    let entity = this._map.get(id);
    if (entity) {
      this._options.update(entity, data);
    } else {
      entity = this._options.create(data);
      this._map.set(id, entity);
      // 重建的实体需要恢复选中/hover/隐藏状态
      if (id === this._selected) entity.setSelected?.(true);
      if (id === this._hovered) entity.setHover?.(true);
      if (!this._visible) entity.setVisible?.(false);
    }
    return entity;
  }

  /** 仅更新已存在的实体，不存在时返回 false */
  updateSingle(data: TData): boolean {
    const entity = this._map.get(this._options.getId(data));
    if (!entity) return false;

    this._options.update(entity, data);
    return true;
  }

  get(id: string) {
    return this._map.get(id);
  }

  values() {
    return this._map.values();
  }

  setSelected(id: string | null) {
    if (id === this._selected) return;

    if (this._selected !== null) {
      this._map.get(this._selected)?.setSelected?.(false);
    }
    this._selected = id;
    if (id !== null) {
      this._map.get(id)?.setSelected?.(true);
    }
  }

  setHover(id: string | null) {
    if (id === this._hovered) return;

    if (this._hovered !== null) {
      this._map.get(this._hovered)?.setHover?.(false);
    }
    this._hovered = id;
    if (id !== null) {
      this._map.get(id)?.setHover?.(true);
    }
  }

  setVisible(value: boolean) {
    if (value === this._visible) return;

    this._visible = value;
    this._map.forEach((entity) => {
      entity.setVisible?.(value);
    });
  }

  dispose() {
    this._map.forEach((entity) => entity.dispose());
    this._map.clear();
    this._selected = null;
    this._hovered = null;
  }
}
